import React, {
  useState, useEffect, useCallback, useRef, useMemo
} from 'react';
import {
  MapContainer, TileLayer, Marker, Popup, useMap
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './StationFinder.scss';
import ReactGA from 'react-ga4';

// ─────────────────────────────────────────
// FIX LEAFLET DEFAULT MARKER ICONS (CRA breaks the default paths)
// ─────────────────────────────────────────
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl:       'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl:     'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const API       = process.env.REACT_APP_API_URL || '';
const PAGE_SIZE = 10;

// ─────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────
const FUEL_META = {
  E10: { name: 'E10 Petrol',    color: '#e8ff47', textColor: '#000', icon: '⛽' },
  E5:  { name: 'Super Unleaded',color: '#4ade80', textColor: '#000', icon: '🏎️' },
  B7:  { name: 'Diesel',        color: '#60a5fa', textColor: '#000', icon: '🚛' },
  SDV: { name: 'Super Diesel',  color: '#f97316', textColor: '#000', icon: '🔴' },
  B10: { name: 'B10 Biodiesel', color: '#a3e635', textColor: '#000', icon: '🌿' },
  HVO: { name: 'HVO Diesel',    color: '#86efac', textColor: '#000', icon: '♻️' },
};

// Brand colours + logo letter(s) for styled badge
// const BRANDS = {
//   Shell:                { color: '#FFD200', bg: '#FFD200',   text: '#B22222', letters: 'SH' },
//   BP:                   { color: '#00A550', bg: '#00A550',   text: '#fff',    letters: 'BP' },
//   Tesco:                { color: '#005EB8', bg: '#005EB8',   text: '#fff',    letters: 'TE' },
//   Asda:                 { color: '#78BE20', bg: '#78BE20',   text: '#fff',    letters: 'AS' },
//   "Sainsbury's":        { color: '#FF6600', bg: '#FF6600',   text: '#fff',    letters: 'SB' },
//   Morrisons:            { color: '#FFD700', bg: '#FFD700',   text: '#006400', letters: 'MO' },
//   Esso:                 { color: '#FF0000', bg: '#FF0000',   text: '#fff',    letters: 'ES' },
//   Jet:                  { color: '#E30B1C', bg: '#E30B1C',   text: '#fff',    letters: 'JT' },
//   Gulf:                 { color: '#F7941D', bg: '#F7941D',   text: '#fff',    letters: 'GU' },
//   Texaco:               { color: '#ED1C24', bg: '#ED1C24',   text: '#fff',    letters: 'TX' },
//   Applegreen:           { color: '#6DC83C', bg: '#6DC83C',   text: '#fff',    letters: 'AG' },
//   Murco:                { color: '#004B8D', bg: '#004B8D',   text: '#fff',    letters: 'MU' },
//   Certas:               { color: '#0072BC', bg: '#0072BC',   text: '#fff',    letters: 'CE' },
//   Moto:                 { color: '#0055a5', bg: '#0055a5',   text: '#fff',    letters: 'MT' },
//   Rontec:               { color: '#e4002b', bg: '#e4002b',   text: '#fff',    letters: 'RO' },
//   'Motor Fuel Group':   { color: '#1a1a2e', bg: '#1a1a2e',   text: '#fff',    letters: 'MFG'},
//   'Ascona Group':       { color: '#4a90d9', bg: '#4a90d9',   text: '#fff',    letters: 'AC' },
// };

const BRANDS = {
  Shell:         { color: '#FFD200', bg: '#FFD200', text: '#B22222', letters: 'SH',
                   logo: 'https://res.cloudinary.com/dsnontai6/image/upload/v1776086473/Shell-Logo_z9jfdr.png' },
  BP:            { color: '#00A550', bg: '#00A550', text: '#fff',    letters: 'BP',
                   logo: 'https://res.cloudinary.com/dsnontai6/image/upload/v1776085678/BP_Helios_logo_v41fco.svg' },
  Tesco:         { color: '#005EB8', bg: '#005EB8', text: '#fff',    letters: 'TE',
                   logo: 'https://res.cloudinary.com/dsnontai6/image/upload/v1776086474/Tesco_Logo.svg_imo34x.png' },
  Asda:          { color: '#78BE20', bg: '#78BE20', text: '#fff',    letters: 'AS',
                   logo: 'https://res.cloudinary.com/dsnontai6/image/upload/v1776086445/ASDA_logo.svg_pvuvxs.png' },
  "Sainsbury's": { color: '#FF6600', bg: '#FF6600', text: '#fff',    letters: 'SB',
                   logo: 'https://res.cloudinary.com/dsnontai6/image/upload/v1776086464/Sainsbury_s_Logo.svg_r6offq.png' },
  Morrisons:     { color: '#FFD700', bg: '#FFD700', text: '#006400', letters: 'MO',
                   logo: 'https://res.cloudinary.com/dsnontai6/image/upload/v1776086654/Wm_Morrison_Supermarkets_logo.svg_l4nbtf.png' },
  Esso:          { color: '#FF0000', bg: '#ED1B2F', text: '#fff',    letters: 'ES',
                   logo: 'https://res.cloudinary.com/dsnontai6/image/upload/v1776086654/Esso_textlogo.svg_rakawe.png' },
  Jet:           { color: '#E30B1C', bg: '#E30B1C', text: '#fff',    letters: 'JT',
                   logo: 'https://res.cloudinary.com/dsnontai6/image/upload/v1776101660/Jet_brand_logo_rm7jvn.png' }, // no Clearbit entry — keep letter badge
  Gulf:          { color: '#F58220', bg: '#F7941D', text: '#fff',    letters: 'GU',
                   logo: 'https://res.cloudinary.com/dsnontai6/image/upload/v1776101719/images_hiwkvm.png' },
  Valero:          { color: '#005EB8', bg: '#005EB8', text: '#fff',    letters: 'VA',
                   logo: 'https://res.cloudinary.com/dsnontai6/image/upload/v1776101709/Valero_Energy_logo.svg_fwsqke.png' },
  "EG ON THE MOVE":          { color: '#78BE20', bg: '#78BE20', text: '#fff',    letters: 'EG',
                   logo: 'https://res.cloudinary.com/dsnontai6/image/upload/v1776101708/EGgroupLogo.svg_p9hpjg.png' },
  Costco:          { color: '#E31837', bg: '#E31837', text: '#fff',    letters: 'CO',
                   logo: 'https://res.cloudinary.com/dsnontai6/image/upload/v1776101708/Costco_Wholesale_logo_2010-10-26.svg_sywpmz.png' },                 
  Essar:          { color: '#E31E24', bg: '#F7941D', text: '#fff',    letters: 'ES',
                   logo: 'https://res.cloudinary.com/dsnontai6/image/upload/v1776086655/Essar_logo.svg_hkqz4s.png' },
  Texaco:        { color: '#ED1C24', bg: '#ED1C24', text: '#fff',    letters: 'TX',
                   logo: 'https://res.cloudinary.com/dsnontai6/image/upload/v1776086438/Texaco-Logo-1981_ioq9zv.jpg' },
  Pace:        { color: '#ED1C24', bg: '#ED1C24', text: '#fff',    letters: 'PA',
                   logo: 'https://res.cloudinary.com/dsnontai6/image/upload/v1776102325/images-2_uitiwm.png' },
  Go:        { color: '#ED1C24', bg: '#ED1C24', text: '#fff',    letters: 'GO',
                   logo: 'https://res.cloudinary.com/dsnontai6/image/upload/v1777997524/images_dze7wi.png' },
};

const AMENITY_MAP = {
  car_wash:        { icon: '🚿', label: 'Car Wash' },
  jet_wash:        { icon: '🫧', label: 'Jet Wash' },
  shop:            { icon: '🛒', label: 'Shop' },
  atm:             { icon: '💳', label: 'ATM' },
  air_pump:        { icon: '🌬️', label: 'Air Pump' },
  ev_charging:     { icon: '⚡', label: 'EV Charging' },
  toilet:          { icon: '🚻', label: 'Toilets' },
  hgv:             { icon: '🚚', label: 'HGV Lane' },
  disabled_access: { icon: '♿', label: 'Disabled Access' },
  loyalty:         { icon: '⭐', label: 'Loyalty Points' },
};

// ─────────────────────────────────────────
// BRAND NAME NORMALISER
// ─────────────────────────────────────────
function normaliseBrand(raw) {
  if (!raw) return raw;
  if (BRANDS[raw]) return raw;
  const lower = raw.toLowerCase();
  const exactCI = Object.keys(BRANDS).find(k => k.toLowerCase() === lower);
  if (exactCI) return exactCI;
  const prefix = Object.keys(BRANDS).find(k => lower.startsWith(k.toLowerCase()));
  if (prefix) return prefix;
  return raw;
}

// ─────────────────────────────────────────
// BRAND LOGO BADGE
// ─────────────────────────────────────────
function BrandBadge({ brand, size = 36 }) {
  const normBrand = normaliseBrand(brand);
  const meta = BRANDS[normBrand] || { bg: '#888', text: '#fff', letters: (brand || '?').slice(0, 2).toUpperCase() };
  console.log('BrandBadge:', normBrand, '→ logo:', meta.logo); 
  const [imgFailed, setImgFailed] = useState(false);

  if (meta.logo && !imgFailed) {
    return (
      <div className="brand-badge" style={{
        width: size, height: size,
        background: '#fff',
        border: `2px solid ${meta.color}`,
        borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden',
        flexShrink: 0,
      }}>
        <img
          src={meta.logo}
          alt={brand}
          width={size * 0.75}
          height={size * 0.75}
          style={{ objectFit: 'contain' }}
          onError={() => setImgFailed(true)}
        />
      </div>
    );
  }

  // Fallback to letter badge
  return (
    <div className="brand-badge" style={{
      width: size, height: size,
      background: meta.bg,
      color: meta.text,
      fontSize: size * 0.28,
    }}>
      {meta.letters}
    </div>
  );
}

// ─────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────
function freshness(updatedAt) {
  if (!updatedAt) return null;
  try {
    const mins = Math.round((Date.now() - new Date(updatedAt).getTime()) / 60_000);
    if (mins < 60)   return `${mins}m ago`;
    if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
    return `${Math.round(mins / 1440)}d ago`;
  } catch { return null; }
}

function getBrandColor(brand) {
  return BRANDS[brand]?.color || '#888';
}

// Build a colour-coded Leaflet icon for a station
// function makeMapIcon(brand, price, activeFuel, cheapestPrice) {
//   const brandColor = getBrandColor(brand);
//   const meta       = BRANDS[brand] || { letters: (brand || '?').slice(0, 2).toUpperCase() };

//   // Colour the price ring: green = at/near cheapest, amber = mid, red = expensive
//   let ringColor = '#888';
//   if (price && cheapestPrice) {
//     const diff = price - cheapestPrice;
//     ringColor = diff < 2 ? '#00c853' : diff < 8 ? '#ff9500' : '#ff3b30';
//   }

//   const svg = `
//     <svg xmlns="http://www.w3.org/2000/svg" width="44" height="52" viewBox="0 0 44 52">
//       <circle cx="22" cy="20" r="18" fill="${brandColor}" stroke="${ringColor}" stroke-width="3"/>
//       <text x="22" y="25" text-anchor="middle" font-family="monospace" font-weight="bold"
//             font-size="11" fill="white" stroke="rgba(0,0,0,0.3)" stroke-width="0.5">${meta.letters}</text>
//       ${price ? `<text x="22" y="38" text-anchor="middle" font-family="monospace" font-size="9"
//                        font-weight="bold" fill="${ringColor}">${price.toFixed(0)}p</text>` : ''}
//       <polygon points="22,50 15,38 29,38" fill="${ringColor}"/>
//     </svg>`;

//   return L.divIcon({
//     html: svg,
//     className: '',
//     iconSize:   [44, 52],
//     iconAnchor: [22, 52],
//     popupAnchor:[0, -52],
//   });
// }

function makeMapIcon(brand, price, activeFuel, cheapestPrice) {
  const normBrand  = normaliseBrand(brand);
  const brandColor = getBrandColor(normBrand);
  const meta       = BRANDS[normBrand] || { letters: (brand || '?').slice(0, 2).toUpperCase() };

  let ringColor = '#888';
  if (price && cheapestPrice) {
    const diff = price - cheapestPrice;
    ringColor = diff < 2 ? '#00c853' : diff < 8 ? '#ff9500' : '#ff3b30';
  }

  // Use logo image inside SVG if available, otherwise fall back to letter badge
  const innerContent = meta.logo
    ? `<circle cx="22" cy="20" r="18" fill="#fff" stroke="${ringColor}" stroke-width="3"/>
       <image href="${meta.logo}" x="5" y="3" width="34" height="34"
              preserveAspectRatio="xMidYMid meet"
              clip-path="url(#circle-clip)"/>`
    : `<circle cx="22" cy="20" r="18" fill="${brandColor}" stroke="${ringColor}" stroke-width="3"/>
       <text x="22" y="25" text-anchor="middle" font-family="monospace" font-weight="bold"
             font-size="11" fill="white" stroke="rgba(0,0,0,0.3)" stroke-width="0.5">${meta.letters}</text>`;

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
         width="44" height="52" viewBox="0 0 44 52">
      <defs>
        <clipPath id="circle-clip">
          <circle cx="22" cy="20" r="17"/>
        </clipPath>
      </defs>
      ${innerContent}
      ${price ? `<text x="22" y="38" text-anchor="middle" font-family="monospace" font-size="9"
                       font-weight="bold" fill="${ringColor}">${price.toFixed(0)}p</text>` : ''}
      <polygon points="22,50 15,38 29,38" fill="${ringColor}"/>
    </svg>`;

  return L.divIcon({
    html: svg,
    className: '',
    iconSize:   [44, 52],
    iconAnchor: [22, 52],
    popupAnchor:[0, -52],
  });
}

// ─────────────────────────────────────────
// MAP BOUNDS FITTER
// ─────────────────────────────────────────
function MapBoundsFitter({ stations, center }) {
  const map = useMap();
  useEffect(() => {
    if (stations.length === 0 && center) {
      map.setView([center.lat, center.lng], 13);
      return;
    }
    if (stations.length > 0) {
      const bounds = L.latLngBounds(stations.map((s) => [s.lat, s.lng]));
      if (center) bounds.extend([center.lat, center.lng]);
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
    }
  }, [stations, center, map]);
  return null;
}

// ─────────────────────────────────────────
// MAP VIEW
// ─────────────────────────────────────────
function MapView({ stations, activeFuel, center, onSelect }) {
  const cheapestPrice = useMemo(() => {
    const prices = stations
      .map((s) => s.fuels[activeFuel]?.price)
      .filter(Boolean);
    return prices.length ? Math.min(...prices) : null;
  }, [stations, activeFuel]);

  if (!center) return null;

  return (
    <div className="finder__map-wrap">
      <MapContainer
        center={[center.lat, center.lng]}
        zoom={12}
        style={{ width: '100%', height: '520px' }}
        className="finder__map"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapBoundsFitter stations={stations} center={center} />

        {/* Search centre marker */}
        <Marker
          position={[center.lat, center.lng]}
          icon={L.divIcon({
            html: '<div class="map-center-dot"></div>',
            className: '',
            iconSize: [16, 16],
            iconAnchor: [8, 8],
          })}
        />

        {stations.map((s) => {
          if (!s.lat || !s.lng) return null;
          const price = s.fuels[activeFuel]?.price;
          return (
            <Marker
              key={s.id}
              position={[s.lat, s.lng]}
              icon={makeMapIcon(s.brand, price, activeFuel, cheapestPrice)}
              eventHandlers={{ click: () => onSelect(s) }}
            >
              <Popup>
                <div className="map-popup">
                  <div className="map-popup__name">{s.name}</div>
                  <div className="map-popup__addr">{s.postcode}</div>
                  {price ? (
                    <div className="map-popup__price"
                         style={{ color: FUEL_META[activeFuel]?.color !== '#e8ff47' ? FUEL_META[activeFuel]?.color : '#666' }}>
                      {FUEL_META[activeFuel]?.icon} {price.toFixed(1)}p/L
                    </div>
                  ) : (
                    <div className="map-popup__price map-popup__price--na">
                      {activeFuel} not available
                    </div>
                  )}
                  <button className="map-popup__btn" onClick={() => onSelect(s)}>
                    View details →
                  </button>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
}

// ─────────────────────────────────────────
// PAGINATION
// ─────────────────────────────────────────
function Pagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;
  const pages = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (page > 3) pages.push('…');
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
    if (page < totalPages - 2) pages.push('…');
    pages.push(totalPages);
  }
  return (
    <nav className="finder__pagination" aria-label="Station pages">
      <button className="finder__pg-btn finder__pg-btn--nav"
              onClick={() => onChange(page - 1)} disabled={page === 1}>← Prev</button>
      <div className="finder__pg-pages">
        {pages.map((p, i) =>
          p === '…'
            ? <span key={`e${i}`} className="finder__pg-ellipsis">…</span>
            : <button key={p}
                      className={`finder__pg-btn${p === page ? ' finder__pg-btn--active' : ''}`}
                      onClick={() => onChange(p)} aria-current={p === page ? 'page' : undefined}>{p}</button>
        )}
      </div>
      <button className="finder__pg-btn finder__pg-btn--nav"
              onClick={() => onChange(page + 1)} disabled={page === totalPages}>Next →</button>
    </nav>
  );
}

// ─────────────────────────────────────────
// STATION DETAIL PANEL
// ─────────────────────────────────────────
function StationPanel({ station, activeFuel, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    ref.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const fuels    = Object.entries(station.fuels).filter(([, f]) => f?.price > 0);
  const cheapest = [...fuels].sort(([, a], [, b]) => a.price - b.price)[0];

  return (
    <>
      <div className="sp-backdrop" onClick={onClose} />
      <aside className="sp" ref={ref} tabIndex={-1} role="dialog" aria-modal="true" aria-label={station.name}>
        <div className="sp__header" style={{ borderTopColor: getBrandColor(station.brand) }}>
          <div className="sp__title-row">
            <BrandBadge brand={station.brand} size={42} />
            <div className="sp__title-info">
              <h2 className="sp__name">{station.name}</h2>
              {station.brand && <p className="sp__brand">{station.brand}</p>}
            </div>
            <button className="sp__close" onClick={onClose} aria-label="Close">✕</button>
          </div>
          <p className="sp__address">📍 {station.address}{station.postcode ? `, ${station.postcode}` : ''}</p>
          {station.distanceMiles != null && <p className="sp__distance">{station.distanceMiles} miles away</p>}
          <a className="sp__directions-btn"
            href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${station.name} ${station.postcode || station.address}`)}`}
            target="_blank" rel="noopener noreferrer"
            onClick={() => ReactGA.event({
              category: 'StationFinder',
              action: 'Get Directions Clicked',
              label: station.name,
            })}>
            🗺️ Get Directions
          </a>
        </div>

        <div className="sp__body">
          <div className="sp__section">
            <h3 className="sp__section-title">⛽ Fuel Prices</h3>
            <div className="sp__fuel-grid">
              {Object.entries(FUEL_META).map(([id, meta]) => {
                const fd  = station.fuels[id];
                const has = fd?.price > 0;
                const age = freshness(fd?.updatedAt);
                return (
                  <div key={id} className={`sp__fuel-card sp__fuel-card--${has ? 'yes' : 'no'}`}
                       style={has ? { borderColor: meta.color } : {}}>
                    <span className="sp__fuel-icon">{meta.icon}</span>
                    <span className="sp__fuel-id">{id}</span>
                    <span className="sp__fuel-name">{meta.name}</span>
                    {has ? <>
                      <span className="sp__fuel-price">{fd.price.toFixed(1)}<small>p/L</small></span>
                      {age && <span className="sp__fuel-age">{age}</span>}
                    </> : <span className="sp__fuel-na">Not available</span>}
                  </div>
                );
              })}
            </div>
            {cheapest && (
              <div className="sp__cheapest-banner">
                💰 Cheapest here: <strong>{FUEL_META[cheapest[0]]?.name || cheapest[0]}</strong> at{' '}
                <strong>{cheapest[1].price.toFixed(1)}p/L</strong>
              </div>
            )}
          </div>

          {station.hours && (
            <div className="sp__section">
              <h3 className="sp__section-title">🕐 Opening Hours</h3>
              <div className="sp__hours">
                {station.hours.split(/[|;]/).map((h, i) => (
                  <p key={i} className="sp__hours-line">{h.trim()}</p>
                ))}
                {/24/i.test(station.hours) && <span className="sp__badge sp__badge--green">Open 24 Hours</span>}
              </div>
            </div>
          )}

          {station.amenities?.length > 0 && (
            <div className="sp__section">
              <h3 className="sp__section-title">🏪 Amenities</h3>
              <div className="sp__amenities">
                {station.amenities.map((a) => {
                  const meta = AMENITY_MAP[a.toLowerCase().replace(/[\s-]+/g, '_')];
                  return meta ? (
                    <span key={a} className="sp__amenity"><span>{meta.icon}</span>{meta.label}</span>
                  ) : null;
                })}
              </div>
            </div>
          )}

          <div className="sp__section sp__section--meta">
            {[
              ['Source', 'UK Government Fuel Finder Scheme'],
              ['Updates within', '30 minutes of any pump change'],
              station.distanceMiles != null && ['Distance', `${station.distanceMiles} miles`],
            ].filter(Boolean).map(([label, val]) => (
              <div key={label} className="sp__meta-row">
                <span>{label}</span><span>{val}</span>
              </div>
            ))}
          </div>
        </div>
      </aside>
    </>
  );
}

// ─────────────────────────────────────────
// STATION CARD (list row)
// ─────────────────────────────────────────
function StationCard({ station, activeFuel, onClick }) {
  const activePrice  = station.fuels[activeFuel]?.price;
  const hasActive    = activePrice > 0;
  const anyMissing   = Object.values(station.fuels).some((f) => !f?.price);
  const knownAmenities = (station.amenities || [])
    .map((a) => AMENITY_MAP[a.toLowerCase().replace(/[\s-]+/g, '_')])
    .filter(Boolean).slice(0, 5);

  return (
    <div
      className={`sc${!hasActive ? ' sc--unavailable' : ''}`}
      onClick={() => {
        ReactGA.event({
          category: 'StationFinder',
          action: 'Station Card Clicked',
          label: station.brand || station.name,
        });
        onClick(station);
      }}
      role="button" tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          ReactGA.event({
            category: 'StationFinder',
            action: 'Station Card Clicked',
            label: station.brand || station.name,
          });
          onClick(station);
        }
      }}
      aria-label={`${station.name} — ${hasActive ? `${activePrice}p` : 'No ' + activeFuel}`}
    >
      <div className="sc__left">
        <BrandBadge brand={station.brand} size={40} />
        <div className="sc__info">
          <div className="sc__name">{station.name}</div>
          <div className="sc__address">{station.address}{station.postcode ? `, ${station.postcode}` : ''}</div>
          {knownAmenities.length > 0 && (
            <div className="sc__amenities">
              {knownAmenities.map((m, i) => (
                <span key={i} className="sc__amenity-badge" title={m.label}>{m.icon}</span>
              ))}
            </div>
          )}
          {station.hours && (
            <div className="sc__hours">
              🕐 {station.hours.length > 50 ? station.hours.slice(0, 50) + '…' : station.hours}
            </div>
          )}
        </div>
      </div>
      <div className="sc__right">
        {station.distanceMiles != null && <div className="sc__dist">{station.distanceMiles} mi</div>}
        <div className={`sc__price-badge ${hasActive ? 'sc__price-badge--yes' : 'sc__price-badge--no'}`}
             style={hasActive ? { background: FUEL_META[activeFuel]?.color } : {}}>
          {hasActive ? `${activePrice.toFixed(1)}p` : 'N/A'}
        </div>
        {anyMissing && hasActive && <div className="sc__partial">Some fuels N/A</div>}
        <div className="sc__tap-hint">Details →</div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────
export default function StationFinder({ searchState }) {
  const [stations,    setStations]    = useState([]);
  const [loadState,   setLoadState]   = useState('idle');
  const [activeFuel,  setActiveFuel]  = useState('E10');
  const [sortBy,      setSortBy]      = useState('distance');   // default nearest
  const [brandFilter, setBrandFilter] = useState('All');
  const [viewMode,    setViewMode]    = useState('list');
  const [selected,    setSelected]    = useState(null);
  const [lastFetch,   setLastFetch]   = useState(null);
  const [page,        setPage]        = useState(1);
  const [searchCenter, setSearchCenter] = useState(null); // { lat, lng }

  const { postcode = '', fuelType = 'E10', radius = 5 } = searchState || {};

  useEffect(() => { setActiveFuel(fuelType); }, [fuelType]);
  useEffect(() => { setPage(1); }, [activeFuel, sortBy, brandFilter]);

  const load = useCallback(async () => {
    if (!postcode) return;
    setLoadState('loading');
    setSelected(null);
    setPage(1);

    try {
      const params = new URLSearchParams({ postcode, radius: String(radius), sort: 'distance', limit: '50' });
      const res    = await fetch(`${API}/api/stations/nearby?${params}`);
      const data   = await res.json();

      if (!res.ok) {
        // Log technical detail to console, show friendly message to user
        console.error('[TTT StationFinder] API error:', data.error || res.status, data);
        throw new Error('user_friendly');
      }

      setStations(data.stations || []);
      if (data.lat && data.lng) setSearchCenter({ lat: data.lat, lng: data.lng });
      setLastFetch(new Date());
      setLoadState('done');
      ReactGA.event({                                        // 👈
        category: 'StationFinder',
        action: 'Search Complete',
        label: `${data.stations?.length || 0} results · ${radius}mi`,
      });
    } catch (err) {
      console.error('[TTT StationFinder] Load failed:', err);
      setLoadState('error');
      ReactGA.event({ category: 'StationFinder', action: 'Search Error' });
    }
  }, [postcode, radius]);

  useEffect(() => { load(); }, [load]);

  // Derived
  const brands  = ['All', ...new Set(stations.map((s) => s.brand).filter(Boolean))].slice(0, 14);

  const visible = useMemo(() =>
    stations
      .filter((s) => brandFilter === 'All' || s.brand === brandFilter)
      .sort((a, b) => {
        if (sortBy === 'price') return (a.fuels[activeFuel]?.price ?? 999) - (b.fuels[activeFuel]?.price ?? 999);
        return (a.distanceMiles ?? 999) - (b.distanceMiles ?? 999);
      }),
    [stations, brandFilter, sortBy, activeFuel]
  );

  const totalPages    = Math.ceil(visible.length / PAGE_SIZE);
  const paginated     = visible.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const availCount    = stations.filter((s) => s.fuels[activeFuel]?.price > 0).length;
  const cheapest      = visible.find((s) => s.fuels[activeFuel]?.price > 0);

  const handlePageChange = (p) => {
    setPage(p);
    document.getElementById('finder')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section id="finder" className="finder">
      <div className="finder__inner">

        <div className="finder__header">
          <p className="finder__label">Fuel Prices Updated Regularly Every 30 Min</p>
          <h2 className="finder__title">Live Station Finder</h2>
          <p className="finder__desc">
            Search by postcode or town name above. Click any station to see full prices,
            opening hours and amenities.
          </p>
        </div>

        {/* Toolbar — only show once data is loaded or loading */}
        {(loadState === 'done' || loadState === 'loading' || loadState === 'error') && (
          <div className="finder__toolbar">
            {/* View mode tabs */}
            <div className="finder__view-tabs">
              <button
                className={`finder__view-tab${viewMode === 'list' ? ' finder__view-tab--active' : ''}`}
                onClick={() => {
                  setViewMode('list');
                  ReactGA.event({ category: 'StationFinder', action: 'View Mode', label: 'List' });
                }}
              >📋 List</button>
              <button
                className={`finder__view-tab${viewMode === 'map' ? ' finder__view-tab--active' : ''}`}
                onClick={() => {
                  setViewMode('map');
                  ReactGA.event({ category: 'StationFinder', action: 'View Mode', label: 'Map' });
                }}
              >🗺️ Map</button>
            </div>

            {/* Fuel filter */}
            <div className="finder__fuel-tabs">
              {Object.entries(FUEL_META).map(([id, meta]) => (
                <button key={id}
                  className={`finder__fuel-tab${activeFuel === id ? ' finder__fuel-tab--active' : ''}`}
                  style={activeFuel === id ? { background: meta.color, borderColor: meta.color, color: '#000' } : {}}
                  onClick={() => setActiveFuel(id)}>
                  {meta.icon} {id}
                </button>
              ))}
            </div>

            <div className="finder__controls">
              <div className="finder__sort-group">
                {[{ val: 'distance', label: '📍 Nearest' }, { val: 'price', label: '💰 Cheapest' }].map(({ val, label }) => (
                  <button key={val}
                    className={`finder__sort-btn${sortBy === val ? ' finder__sort-btn--active' : ''}`}
                    onClick={() => {
                      setSortBy(val);
                      ReactGA.event({ category: 'StationFinder', action: 'Sort Changed', label: val });
                    }}>{label}</button>
                ))}
              </div>

              {brands.length > 1 && (
                <select className="finder__brand-select" value={brandFilter}
                        onChange={(e) => setBrandFilter(e.target.value)} aria-label="Filter by brand">
                  {brands.map((b) => (
                    <option key={b} value={b}>{b === 'All' ? 'All Brands' : b}</option>
                  ))}
                </select>
              )}

              <button className="finder__refresh" onClick={load}
                      disabled={loadState === 'loading'} title="Refresh" aria-label="Refresh stations">
                {loadState === 'loading' ? '⟳' : '↺'}
              </button>
            </div>
          </div>
        )}

        {/* Status bar */}
        {loadState === 'done' && (
          <div className="finder__status-bar">
            <span className="finder__status-count">{visible.length} stations · {availCount} have {activeFuel}</span>
            {cheapest && sortBy === 'price' && (
              <span className="finder__status-cheapest">
                Cheapest: <strong>{cheapest.fuels[activeFuel].price.toFixed(1)}p/L</strong> at {cheapest.name}
              </span>
            )}
            {lastFetch && (
              <span className="finder__status-time">
                Fetched {lastFetch.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>
        )}

        {/* ── IDLE — prompt user to search */}
        {loadState === 'idle' && (
          <div className="finder__prompt">
            <div className="finder__prompt-icon">📍</div>
            <h3 className="finder__prompt-title">Find Fuel Stations Near You</h3>
            <p className="finder__prompt-text">
              Enter a <strong>postcode</strong> (e.g. <em>L1 8LT</em>) or a <strong>town name</strong>{' '}
              (e.g. <em>Liverpool</em>) in the search bar above, or tap{' '}
              <strong>Use My Location</strong> to find stations around you automatically.
            </p>
          </div>
        )}

        {/* ── LOADING — spinner */}
        {loadState === 'loading' && (
          <div className="finder__loading">
            <div className="finder__loading-spinner">
              <div className="finder__loading-ring" />
            </div>
            <p className="finder__loading-text">Finding stations near you…</p>
            <p className="finder__loading-sub">Loading from UK Government Fuel Finder</p>
          </div>
        )}

        {/* ── ERROR — friendly message, technical detail in console */}
        {loadState === 'error' && (
          <div className="finder__error">
            <div className="finder__error-icon">⚠️</div>
            <div className="finder__error-body">
              <strong>Sorry, we couldn't load fuel stations right now.</strong>
              <p>Please try again in a moment. If the problem continues, try a different postcode or location.</p>
            </div>
            <button className="finder__error-btn" onClick={load}>Try Again →</button>
          </div>
        )}

        {/* ── DONE — Map or List */}
        {loadState === 'done' && (
          <>
            {viewMode === 'map' && searchCenter && (
              <MapView
                stations={visible}
                activeFuel={activeFuel}
                center={searchCenter}
                onSelect={setSelected}
              />
            )}

            {viewMode === 'list' && (
              <>
                <div className="finder__list">
                  {visible.length === 0 ? (
                    <div className="finder__empty">
                      No stations found within {radius} miles.
                      {brandFilter !== 'All' && ' Try removing the brand filter.'}
                    </div>
                  ) : (
                    paginated.map((s) => (
                      <StationCard key={s.id} station={s} activeFuel={activeFuel} onClick={setSelected} />
                    ))
                  )}
                </div>
                <Pagination page={page} totalPages={totalPages} onChange={handlePageChange} />
              </>
            )}
          </>
        )}

        {/* Stale data disclaimer */}
        {loadState === 'done' && (
          <div className="finder__stale-warning">
            <span>⚠️</span>
            <span>
              <strong>Availability not guaranteed.</strong> Stations whose price data is
              more than 12 hours old may be out of stock. Call ahead before making a special trip.
            </span>
          </div>
        )}

        <p className="finder__attribution">
          Data from the{' '}
          <a href="https://www.gov.uk/guidance/access-fuel-price-data" target="_blank" rel="noopener noreferrer">
            UK Government Fuel Finder Scheme
          </a>{' '}· Prices updated within 30 minutes of any pump change
        </p>
      </div>

      {selected && (
        <StationPanel station={selected} activeFuel={activeFuel} onClose={() => setSelected(null)} />
      )}
    </section>
  );
}
